import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import {
  FormattedDate,
  FormattedTime,
  FormattedMessage,
  injectIntl,
} from 'react-intl';
import { isAgencyEnabled } from '@mummssoftware/utils/utils';
import { withStyles } from '@material-ui/core/styles';
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';
import { Grid } from '@material-ui/core';
import {
  ActionIcon,
  ExpansionPanel,
  ExpansionPanelDetails,
  ExpansionPanelSummary,
  gridContainerStyle as gridContainer,
  SectionHeader,
  VisitModal,
} from '@mummssoftware/common-ui';
import { checkIfPecosApproved } from '@mummssoftware/utils/pecos';
import { Listing, Card } from '@mummssoftware/utils/constants';
import { formatPhysicianName } from '@mummssoftware/utils/formatters';
import { getVisitByPersonAndVisitId } from '@mummssoftware/utils/pimReader';
import Manifest from '@mummssoftware/manifest';
import messages from './CertMessages';
import { CertHeader, CertNav, CertBody, CertFooter } from './Components';
// TODO a shared component folder with snackbar?
import { CertSnackBar } from '../Listing/Components/Snackbars';
import { withCertContext } from '../../Context';
import { formatNarrativeVal } from './Components/utils';
import { getCurrentDoctorFromList, getDoctorNpi } from '../utils/utils';
import {
  dateFormat,
  ATTENDING_FORM,
  FORM_NARRATIVE,
  NO_ATTENDING,
  SET_DIMENSIONS,
  SET_VERBAL_DATE,
  SET_BUTTON_CLICKED,
  RTF_BLANK_STRING,
} from '../../constants';

// NOTE Overriding styles of Material UI with method 1 : class names, used with withStyles and className
// https://material-ui.com/customization/components/
const styles = (theme) => ({
  root: (props) =>
    props.printConfig
      ? {
          '&': {
            padding: '4px 0',
          },
        }
      : {
          '&': {
            padding: '4px 0',
            minHeight: '150px',
          },
        },
  label: {
    color: theme.palette.text.hint,
    ...theme.props.printRules.textSecondary,
  },
  updatableField: {
    transition: 'text-shadow 0.75s ease-out',
  },
  updatedField: {
    textShadow: `0px 3px 12px ${theme.palette.primary.main},
    0px -3px 12px ${theme.palette.primary.main},
    3px 0px 12px ${theme.palette.primary.main},
    -3px 0px 12px ${theme.palette.primary.main}
    `,
  },
  control: {
    padding: theme.spacing(2),
  },
  copyIcon: {
    padding: 0,
    margin: 6,
    height: 36,
    width: 36,
    position: 'absolute',
    right: 10,
    top: 4,
  },
  printItems: {
    whiteSpace: 'nowrap',
  },
  gridContainer: {
    ...gridContainer,
    height: 'max-content',
  },
  gridContainerBody: {
    ...gridContainer,
    '@media print': {
      '&&': { paddingTop: 12 },
    },
  },
  errorMessage: {
    color: theme.palette.error.main,
    '@media print': {
      display: 'none',
    },
  },
  loaderGridContainerBody: {
    '&&': {
      overflow: 'hidden',
      minHeight: 200,
      backgroundImage: `linear-gradient(
                          to bottom,
                          rgba(0, 0, 0, 0) 1%,
                          rgba(0, 0, 0, 0.25) 4%,
                          rgba(0, 0, 0, 0.25) 96%,
                          rgba(0, 0, 0, 0) 98%)`,
      margin: 0,
      padding: 18,
    },
    '&& > *': {
      padding: 12,
    },
  },
  hide: {
    display: 'none',
  },
  certFooter: {
    '&&': {
      padding: '4px',
    },
  },
});

/**
 * @typedef CertFormProps
 * @prop {object} classes
 * @prop {import('../SharedPropDefs').Certification} currentCert
 * @prop {object} currentCertKeys
 * @prop {boolean} [disableForward]
 * @prop {boolean} [disableBack]
 * @prop {boolean} [expandable] Sets whether form is expandable or not
 * @prop {string} formType
 * @prop {string} formPhysId
 * @prop {boolean} formPhysRestricted
 * @prop {boolean} [historicalView]
 * @prop {boolean} [isDoctor]
 * @prop {function} onNavigateCert
 * @prop {function} onHide
 * @prop {import('../SharedPropDefs').Patient} _patient Prefixed with an underscore so it is not overwritten by the value from CertContext (notice we wrao the component in withCertContext)
 * @prop {number} selectedCertIndex
 * @prop {boolean} submitting
 * @prop {function} updateUserInfo
 * @prop {string[]} updates
 * @prop {string} userId
 * @prop {boolean} embedded
 */
/**
 *
 * @extends {React.Component<CertFormProps, {}>}
 */
const Footer = ({
  alreadyCertified,
  certificationDateTime,
  classes,
  certPhysName,
  footerType,
  formNarrativeId,
  formType, // ok
  hideFooter,
  selectedCertIndex,
  updates,
  smallWidget,
  trailingMessage,
  printConfig,
  historicalView,
}) =>
  hideFooter ? null : (
    <Grid
      className={`${classes.gridContainer} ${classes.certFooter}`}
      container
      item
      xs={12}
    >
      <CertFooter
        phys={certPhysName}
        footer={footerType}
        alreadyCertified={alreadyCertified}
        smallWidget={smallWidget}
        trailingMessage={trailingMessage}
        printConfig={printConfig}
        formType={formType}
        historicalView={historicalView}
        {...{
          classes,
          certificationDateTime,
          formNarrativeId,
          formType,
          selectedCertIndex,
          updates,
        }}
      />
    </Grid>
  );

/**
 * Travis OP-30575 5/15/24
 * This class has a member variable initialRender that tracks if this is the first render
 * for the instance of the class. This was done because with the guards around the
 * SET_VERBAL_DATE dispatch, the verbald date did not get dispatched correctly for the
 * first cert to display to have a verbal date.  This hack was done therefor to make sure
 * that the dispatch happens at least once on the initial render
 * */
class CertForm extends PureComponent {
  initialRender;

  constructor(props) {
    super(props);
    this.initialRender = true;
    const {
      currentCertKeys: { formNarrative },
      selectedCertIndex,
    } = this.props;
    this.certCache = new Array(selectedCertIndex);

    this.state = {
      expanded: true,
      narrative: this.getCertNarrative(props.currentCert, formNarrative),
      textCopy: '',
      visitDetails: null,
      visitDetailsOpen: false,
      showDoctorPecosMessage: false,
    };

    if (props.isDoctor === null) {
      props.updateUserInfo();
    }
    // NOTE: when cert-app is embedded need to know where to locate the physician picker on the page
    // position is calculated when clicking on the button, default is middle of page
    this.refCallback = (element) => {
      if (element) {
        this.element = element;
        const dimensions = element.getBoundingClientRect();
        props.dispatch({
          type: SET_DIMENSIONS,
          payload: dimensions,
        });
      }
    };
  }

  static defaultProps = {
    updates: [],
  };

  getCertNarrative = (cert, formNarrative) =>
    cert[formNarrative] === null ? '' : cert[formNarrative];

  setRecordAll = (
    attendingPhysicianId,
    electionNumber,
    attendingPhysWrittenDate,
  ) =>
    attendingPhysicianId === NO_ATTENDING &&
    electionNumber === 1 &&
    !attendingPhysWrittenDate;

  // OP-33276: `clicked` is the dataset of the Verbal/Sign button. It is passed
  // explicitly when a late cert is confirmed from the warning dialog, where the
  // original button is no longer the event target.
  handleCertify = (e, clicked = e.target.closest('button').dataset) => {
    const dimensions = this.element.getBoundingClientRect();
    // NOTE: when cert-app is embedded need to know where to locate the physician picker on the page
    // position is calculated when clicking on the button
    if (this.props.embedded && dimensions && dimensions.width > 500) {
      this.props.dispatch({
        type: SET_DIMENSIONS,
        payload: null,
      });
    }
    const buttonClicked =
      clicked.endpoint === 'recordverbal' ? 'verbal approval' : 'signature';
    this.props.dispatch({
      type: SET_BUTTON_CLICKED,
      payload: buttonClicked,
    });
    this.props.onCertify(
      this.state.narrative,
      this.props.currentCert.electionperiodId,
      this.props.formType === ATTENDING_FORM,
      this.setRecordAll(
        this.props.currentCert.episode.attendingPhysicianId,
        this.props.currentCert.electionNumber,
        this.props.currentCert.attendingPhysWrittenDate,
      ),
      clicked && clicked.endpoint,
      clicked && clicked.successmsg,
      this.props.currentCert.episode.attendingPhysicianId,
      this.props.userId,
      this.props.currentCert,
    );
  };

  handleChanges = (prevCert) => {
    const { currentCert, currentCertKeys } = this.props;
    const updates = Object.entries(currentCertKeys).reduce((newArr, [k, v]) => {
      if (k === 'formPhys' && prevCert[v].id !== currentCert[v].id) {
        newArr.push(k);
      } else if (prevCert[v] !== currentCert[v]) {
        newArr.push(k);
      }
      return newArr;
    }, []);
    this.setState(
      (prevState) => ({
        narrative: updates.includes(FORM_NARRATIVE)
          ? currentCert[currentCertKeys.formNarrative]
          : prevState.narrative,
      }),
      () => {
        setTimeout(() => this.props.setUpdatedKeys(updates, 250));
      },
    );
  };

  handleExpand = () => {
    this.setState(({ expanded }) => ({ expanded: !expanded }));
  };

  captureNarrative = (name) => (html) => {
    this.setState({
      [name]: html,
    });
  };

  copyText = () => {
    document.querySelector('#narrative-field').select();
    document.execCommand('copy');
    this.setState((prevState) => ({
      textCopy: prevState.narrative,
      openSnackBar: true,
    }));
  };

  closeSnackbar = () => {
    this.setState({ openSnackBar: false });
  };

  pasteText = () => {
    this.setState((prevState) => ({
      narrative: prevState.textCopy,
      textCopy: '',
    }));
  };

  certPhysName = (alreadyCertified) => {
    const {
      currentCertKeys,
      isDoctor,
      firstName,
      lastName,
      userCredentials,
      currentCert: { [currentCertKeys.formPhys]: formPhys },
    } = this.props;
    let certPhysName = '';
    if (isDoctor && !alreadyCertified && !currentCertKeys.useFormPhys) {
      certPhysName = formatPhysicianName(
        '',
        firstName,
        lastName,
        userCredentials,
      );
    } else {
      certPhysName = formatPhysicianName(
        '',
        formPhys.firstName,
        formPhys.lastName,
        formPhys.credentials,
      );
    }
    return certPhysName;
  };

  isF2fRequired = () => {
    const { currentCert } = this.props;
    const { electionNumber, electionNumberForF2F } = currentCert;
    return (
      typeof electionNumberForF2F === 'number' &&
      electionNumber >= electionNumberForF2F
    );
  };

  /**
   * Returns the f2f date in local time or null if
   * there is no f2f date.  Returns null if f2f is not
   * required for the current cert
   */
  getF2fDate = () => {
    const {
      currentCert: { face2faceVisit },
    } = this.props;
    if (!face2faceVisit) return null;

    const { siteTimezone, effectiveDateUTC } = face2faceVisit;
    const siteTz = siteTimezone || moment.tz.guess();

    if (!effectiveDateUTC) return null;

    return effectiveDateUTC
      ? moment
          .utc(effectiveDateUTC)
          // if siteTimezone === null, format breaks.
          .tz(siteTz)
      : '';
  };

  getF2fDetails = () => {
    const {
      currentCert: {
        electionNumber,
        electionNumberForF2F,
        face2faceVisit: {
          siteTimezone,
          effectiveDateUTC,
          provider,
          visit_id: visitId,
        },
      },
    } = this.props;
    const siteTz = siteTimezone || moment.tz.guess();
    const required =
      typeof electionNumberForF2F === 'number' &&
      electionNumber >= electionNumberForF2F;
    /* eslint-disable indent */
    const f2fDateTime = effectiveDateUTC
      ? moment
          .utc(effectiveDateUTC)
          // if siteTimezone === null, format breaks.
          .tz(siteTz)
      : '';
    const linkText = <FormattedMessage {...messages.f2fLink} />;
    /* eslint-enable */
    return {
      missing: required && visitId === null,
      visitId,
      date: f2fDateTime ? (
        <FormattedDate value={f2fDateTime} format="long" timeZone={siteTz} />
      ) : null,
      link: linkText,
      provider,
      required,
    };
  };

  setCanCertify = (useFormPhys, userId, formPhysId, formPhysRestricted) =>
    !(userId === formPhysId && formPhysRestricted) &&
    !(useFormPhys && userId !== formPhysId && !formPhysRestricted) &&
    !this.state?.showDoctorPecosMessage;

  setBody = (f2fMissing, noAttendingRequired, inDateRange) => {
    const { verbalOnly } = this.props;
    let select = 'default';
    if (this.props.formType === ATTENDING_FORM && noAttendingRequired) {
      select = 'noAttendingPecos';
    }
    // TD 4-23-25: leaving this here in case we need to come back and dif
    // between normal no attending  and pecos specific no attending.
    // else if (
    //   this.props.formType === ATTENDING_FORM &&
    //   this.state.showAttendingPecosIneligibleMessgae
    // )
    // {
    //     select = 'noAttendingPecos';
    //   }
    else if (this.state.showDoctorPecosMessage) {
      select = 'notPecosApproved';
    } else if (f2fMissing || !inDateRange) {
      select = 'errorState';
    } else if (verbalOnly) {
      select = 'verbalOnly';
    }
    return select;
  };

  setFooter = (
    isDoctor,
    alreadyCertified,
    f2fMissing,
    transferredAndVerified,
    noAttendingRequired,
    formType,
  ) => {
    let select = 'default';
    if (transferredAndVerified) {
      return select;
    } else if (alreadyCertified) {
      select = 'certified';
    } else if (
      (!isDoctor && !f2fMissing) ||
      (formType === ATTENDING_FORM && !noAttendingRequired)
    ) {
      select = 'print';
    }
    return select;
  };

  setErrors = (f2fMissing, inDateRange) => ({
    outOfRange: !inDateRange,
    f2fMissing,
  });

  headerTitle = () => {
    const {
      formType: regularFormtype,
      currentCert: { electionNumber, showattending },
      printFormType,
    } = this.props;
    const formType = this.props.printConfig ? printFormType : regularFormtype;
    const baseTitle = this.props.intl.formatMessage(messages.Certification);
    if (electionNumber === 1 || showattending) {
      return formType === ATTENDING_FORM ? (
        <FormattedMessage {...messages.attendingTitle} values={{ baseTitle }} />
      ) : (
        <FormattedMessage {...messages.hospiceTitle} values={{ baseTitle }} />
      );
    }
    return baseTitle;
  };

  hideCert = (alreadyCertified, body, noNarrative, f2fMissing) =>
    /** Don't hide if it's still checking... */
    (!(body === 'loader') &&
      /** Go ahead though, if it's already certified, and there's no narrative. */
      alreadyCertified &&
      noNarrative) ||
    /** Or if there's no f2f. That's no bueno, right? */
    f2fMissing;

  doPhysicianPecosCheck = async () => {
    const {
      userId,
      doctors,
      formType,
      pim,
      getAgency,
      isDoctor,
      currentCert,
      currentCertKeys,
    } = this.props;
    const { formPhysWrittenDate } = currentCertKeys;

    const missingF2f = this.isF2fRequired() && !this.getF2fDate();

    // if formWrittenDate, then certs been signed, no reason to do pecos check
    // If cert needs f2f, show f2f message instead of pecos.
    if (missingF2f) {
      this.setState({ showDoctorPecosMessage: false });
      return;
    }
    const writtenDate = formPhysWrittenDate
      ? currentCert[formPhysWrittenDate]
      : null;

    if (!isDoctor) {
      this.setState({ showDoctorPecosMessage: false });
    } else if (userId && doctors && formType && !writtenDate) {
      const currentDoctor = getCurrentDoctorFromList(userId, doctors[formType]);

      let npi = currentDoctor?.npi || '';
      // Attending physicians do not have the npi property, so it has to be fetched
      // in those cases.
      if (!npi) {
        npi = await getDoctorNpi(userId, pim);
      }
      const today = moment().utc().format('YYYY-MM-DD');
      const ignorePECOS = isAgencyEnabled(window.env.ignorePECOSRequirement, {
        currentHospiceBin: getAgency(),
      });
      const isPecosApproved = ignorePECOS
        ? true
        : await checkIfPecosApproved(npi, pim, today, getAgency());
      this.setState({ showDoctorPecosMessage: !isPecosApproved });
    } else {
      this.setState({ showDoctorPecosMessage: false });
    }
  };

  componentDidMount = async () => {
    this.doPhysicianPecosCheck();
    if (this.props.currentCert.face2faceVisit && !this.state.fetchingDetails) {
      // OP-33283: fetchFaceToFaceDetails sets fetchingDetails only when it actually fetches.
      // Setting it here stuck it at true when the first cert shown had no F2F visit yet
      // (visit_id / provider.personNumber null), so earlier certs' F2F popups never loaded.
      const {
        visit_id: visitId,
        provider,
      } = this.props.currentCert.face2faceVisit;
      this.fetchFaceToFaceDetails(visitId, provider);
    }
  };

  /**
   * Diffs prev and current current cert by
   * insuranceCarrierId
   * electionNumber
   * certificationId
   * formType
   */
  currentCertChanged = (prevProps) => {
    if (!prevProps) {
      return true;
    }
    const { currentCert: prevCert, formType: pFormType } = prevProps;
    const { currentCert, formType } = this.props;
    const {
      insuranceCarrierId: pInsuranceCarrierId,
      electionNumber: pElectionNumber,
      certificationId: pCertificationId,
    } = prevCert;
    const { insuranceCarrierId, electionNumber, certificationId } = currentCert;

    return (
      pInsuranceCarrierId !== insuranceCarrierId ||
      pElectionNumber !== electionNumber ||
      pCertificationId !== certificationId ||
      pFormType !== formType
    );
  };

  checkDocListUpdate = (prevDoctors, currentDoctors) =>
    prevDoctors.ATTENDING?.length !== currentDoctors.ATTENDING?.length ||
    prevDoctors.CERTIFYING?.length !== currentDoctors.CERTIFYING?.length;

  async componentDidUpdate(prevProps) {
    const {
      currentCert,
      expanded,
      selectedCertIndex,
      formType,
      currentCertKeys,
      isDoctor,
    } = this.props;

    if (
      this.props.currentCert.face2faceVisit &&
      !this.state.visitDetails &&
      !this.state.fetchingDetails
    ) {
      const {
        visit_id: visitId,
        provider,
      } = this.props.currentCert.face2faceVisit;
      // problem repeated API call as no visitDetails
      const didUpdateCycle = true;
      this.fetchFaceToFaceDetails(visitId, provider, didUpdateCycle);
    }

    if (expanded !== this.state.expanded && expanded !== prevProps.expanded) {
      this.handleExpand();
    }
    if (
      prevProps.selectedCertIndex !== selectedCertIndex ||
      prevProps.formType !== formType
    ) {
      // eslint-disable-next-line
      this.setState({
        narrative: this.getCertNarrative(
          currentCert,
          currentCertKeys.formNarrative,
        ),
      });
    } else if (
      prevProps.currentCert !== currentCert &&
      prevProps.currentCert.electionperiodId === currentCert.electionperiodId
    ) {
      this.handleChanges(prevProps.currentCert);
    }
    // This is triggering excessive re-renders
    const formPhysVerbalDate = currentCert[currentCertKeys.formPhysVerbalDate];
    const prevPhysVerbalDate =
      prevProps.currentCert[currentCertKeys.formPhysVerbalDate];
    const currentFormID = `${currentCert.carrierName}-${currentCert.electionNumber}`;
    const prevFormID = `${prevProps.currentCert.carrierName}-${prevProps.currentCert.electionNumber}`;

    const changeInFormType = prevProps.formType !== this.props.formType;

    if (
      this.initialRender ||
      changeInFormType ||
      currentFormID !== prevFormID ||
      formPhysVerbalDate !== prevPhysVerbalDate
    ) {
      this.props.dispatch({
        type: SET_VERBAL_DATE,
        payload: formPhysVerbalDate,
      });
      this.initialRender = false;
    }
    if (this.currentCertChanged(prevProps)) {
      if (isDoctor) {
        this.doPhysicianPecosCheck();
      }
    }
  }

  /**
   * OP-33283: CertContext's agency is only the URL `?agency=` / saved support agency, so it is null
   * for a regular standalone login. App's getAgency() resolves the user's hospice bin; without it the
   * F2F popup rendered its forms as plain text (VisitContents needs an agency to build form links).
   */
  getF2fAgency = () => {
    const { getAgency, agency } = this.props;
    return (getAgency && getAgency()) || agency;
  };

  fetchFaceToFaceDetails = async (visitId, provider, updateCycle) => {
    if (provider.personNumber && visitId) {
      this.setState({
        fetchingDetails: true,
      });
      const visitDetails = await getVisitByPersonAndVisitId(
        provider.personNumber,
        visitId,
        this.getF2fAgency(),
        this.props.keycloak,
        this.props.patient.patientNumber,
      );
      // case scenario when there is no visitDetails
      const visitDetailsRes =
        visitDetails && visitDetails.length ? visitDetails[0] : {};
      const visitAns = updateCycle ? visitDetailsRes : visitDetails[0];
      this.setState({
        visitDetails: visitAns,
        fetchingDetails: false,
      });
    }
  };

  /**
   * Pecos Check specificly for Attending Physician.  In the event that the attending
   * has not been set, will return true.
   * @returns bool
   */
  isAttendingPecosEligible = async () => {
    const { formType, currentCert, pim, getAgency } = this.props;

    let approved = false;
    if (formType === ATTENDING_FORM) {
      const { attendingPhys } = currentCert;

      if (!attendingPhys?.id) {
        return true;
      }
      if (attendingPhys?.id === NO_ATTENDING) {
        return false;
      }

      const attendingPersonId = attendingPhys?.id || null;
      let npi = attendingPhys?.npi || '';
      // Attending physicians do not have the npi property, so it has to be fetched
      // in those cases.
      if (!npi) {
        npi = await getDoctorNpi(attendingPersonId, pim);
      }
      const today = moment().utc().format('YYYY-MM-DD');
      const ignorePECOS = isAgencyEnabled(window.env.ignorePECOSRequirement, {
        currentHospiceBin: getAgency(),
      });
      const isPecosApproved = ignorePECOS
        ? true
        : await checkIfPecosApproved(npi, pim, today, getAgency());

      approved = isPecosApproved;
    }
    return approved;
  };

  render() {
    const {
      classes,
      currentCert,
      currentCertKeys,
      currentCert: {
        actualEndDate,
        attendingPhys,
        attendingPhysWrittenDate,
        attendingTakenByFirstName,
        attendingTakenByLastName,
        electionNumber,
        attendingPhys: { id },
        episode: { attendingPhysicianId, admitDate },
        expectedEndDate,
        [currentCertKeys.formPhys]: formPhys,
        [currentCertKeys.formPhysWrittenDate]: formPhysWrittenDate,
        [currentCertKeys.formPhysVerbalDate]: formPhysVerbalDate,
        [currentCertKeys.formNarrativeCreationDate]: formNarrativeCreationDate,
        [currentCertKeys.formNarrativeId]: formNarrativeId,
        [currentCertKeys.formTakenByFirstName]: formTakenByFirstName,
        [currentCertKeys.formTakenByLastName]: formTakenByLastName,
        [currentCertKeys.formVerbalTakenByFirstName]: formVerbalTakenByFirstName,
        [currentCertKeys.formVerbalTakenByLastName]: formVerbalTakenByLastName,
        [currentCertKeys.formVerbalTakenByCredentials]: formVerbalTakenByCredentials,
        [currentCertKeys.formpastingofnarratives]: formpastingofnarratives,
        [currentCertKeys.formadditionalnarrative]: formadditionalnarrative,
        hospicePhysWrittenDate,
        narrativeSiteTimezone,
        priorHospice,
        startDate,
        documentationVerified,
        showattending,
        hospiceTakenByFirstName,
        hospiceTakenByLastName,
        face2faceVisit,
      },
      disableForward,
      disableBack,
      expandable,
      formPhysId,
      formPhysRestricted,
      formType,
      historicalView,
      intl: { formatDate, formatMessage },
      isDoctor,
      // onHide,
      onNavigateCert,
      selectedCertIndex,
      submitting,
      userId,
      updates,
      embedded,
      smallWidget,
      _patient,
      keycloak,
      printConfig,
      printFormType,
    } = this.props;

    const { visitDetails, visitDetailsOpen } = this.state;
    const noAttendingRequired = id === NO_ATTENDING;
    const siteTz = narrativeSiteTimezone || moment.tz.guess();

    const canCertify = this.setCanCertify(
      currentCertKeys.useFormPhys,
      userId,
      formPhys.id || formPhysId,
      formPhysRestricted,
    );
    const transferredAndVerified = !!(
      priorHospice === 'Transferred' && documentationVerified
    );
    const f2fDetails = this.getF2fDetails();
    const pastingofnarratives = formpastingofnarratives;
    const additionalnarrative = formadditionalnarrative;

    const noNarrative =
      !this.state.narrative || this.state.narrative === RTF_BLANK_STRING;
    const alreadyCertified = formPhysWrittenDate !== null;
    const certPhysName = this.certPhysName(alreadyCertified);
    const benStartDate = moment.utc(startDate);
    const benEndDate = moment.utc(actualEndDate || expectedEndDate);
    const benPeriod =
      moment.duration(benEndDate.diff(benStartDate)).asDays() + 1;
    const attendingRequired =
      (electionNumber === 1 || showattending) &&
      attendingPhysWrittenDate === null &&
      !noAttendingRequired;
    const inDateRange = benStartDate.diff(moment(), 'days') < 15;
    let certificationDateTime;
    let takenBy;
    if (formPhysWrittenDate) {
      certificationDateTime = (
        <FormattedDate
          value={moment.utc(formPhysWrittenDate)}
          {...dateFormat}
        />
      );
    } else if (formNarrativeCreationDate) {
      const time = moment.utc(formNarrativeCreationDate).tz(siteTz);
      certificationDateTime = (
        <FormattedMessage
          {...messages.signedOn}
          values={{
            date: (
              <FormattedDate value={time} format="long" timeZone={siteTz} />
            ),
            time: <FormattedTime value={time} timeZone={siteTz} />,
          }}
        />
      );
    }
    if ((formTakenByFirstName || formTakenByLastName) && formPhysWrittenDate) {
      takenBy = formatMessage(messages.onDate, {
        msg: formatMessage(messages.recordedBy, {
          name: `${formTakenByFirstName} ${formTakenByLastName}`.trim(),
        }),
        date: formatDate(formPhysWrittenDate, dateFormat),
      });
    }

    let isSelfSigned = false;

    // OP-31347: fix hard copy sig not showing on cert
    /* OP-32032: 'formType' prop passed both by parent component and cert context provider.
    Value from context is initialized to 'CERTIFYING" which overrides the parent's correct
    formType got from an API call. Parent already passed a 'printFormType' variable, which
    seems to be there for this reason, but wasn't used in this block. Added that logic here.
    */
    const currFormType = this.props.printConfig ? printFormType : formType;
    if (currFormType === 'ATTENDING' && attendingPhysWrittenDate) {
      isSelfSigned =
        attendingTakenByFirstName === null
          ? true
          : `${attendingPhys?.firstName} ${attendingPhys?.lastName}` ===
            `${attendingTakenByFirstName} ${attendingTakenByLastName}`;
    } else if (currFormType === 'CERTIFYING' && hospicePhysWrittenDate) {
      isSelfSigned =
        hospiceTakenByFirstName === null
          ? true
          : `${currentCert?.hospicePhys?.firstName} ${currentCert?.hospicePhys?.lastName}` ===
            `${hospiceTakenByFirstName} ${hospiceTakenByLastName}`;
    }

    let trailingMessage;
    if (alreadyCertified) {
      if (isSelfSigned) {
        trailingMessage = formatMessage(messages.electronicallySigned, {
          phys: certPhysName,
          certificationDateTime,
        });
      } else {
        trailingMessage = formatNarrativeVal(formatMessage, takenBy);
      }
    } else {
      trailingMessage = null;
    }

    const headerGrids = {
      sm: 6,
      md: 4,
    };
    const HeaderTitle = () => (
      <Fragment>
        {this.headerTitle()} #{electionNumber}{' '}
        <span style={{ display: 'inline-block' }}>
          <FormattedMessage
            {...messages.headerDays}
            values={{ days: benPeriod }}
          />
        </span>
      </Fragment>
    );
    const body = this.setBody(
      f2fDetails.missing,
      noAttendingRequired,
      inDateRange,
    );
    const footerType = this.setFooter(
      isDoctor,
      alreadyCertified,
      f2fDetails.missing,
      transferredAndVerified,
      noAttendingRequired,
      formType, // ok
    );
    const hideFooter =
      printConfig && footerType !== 'print'
        ? false
        : footerType === 'default' ||
          (alreadyCertified && noNarrative) ||
          body === 'verbalOnly';
    return (
      <Fragment>
        <ExpansionPanel
          expanded={!expandable || this.state.expanded}
          onChange={this.handleExpand}
        >
          <ExpansionPanelSummary noCursor={!expandable}>
            <SectionHeader
              action={
                // TODO: Get this outta here
                <Fragment>
                  {expandable ? (
                    <ActionIcon
                      title={
                        this.state.expanded
                          ? this.props.intl.formatMessage({ id: 'Collapse' })
                          : this.props.intl.formatMessage({ id: 'Expand' })
                      }
                      Icon={
                        this.state.expanded
                          ? KeyboardArrowDown
                          : KeyboardArrowRight
                      }
                    />
                  ) : null}
                  {historicalView ? null : (
                    <CertNav
                      classes={classes}
                      disableBack={disableBack}
                      disableForward={disableForward}
                      formatMessage={this.props.intl.formatMessage}
                      onNavigateCert={(position) => {
                        this.setState({
                          visitDetails: null,
                          visitDetailsOpen: false,
                        });
                        return onNavigateCert(position);
                      }}
                      doPhysicianPecosCheck={this.doPhysicianPecosCheck}
                      isDoctor={isDoctor}
                    />
                  )}
                </Fragment>
              }
              title={<HeaderTitle />}
            />
          </ExpansionPanelSummary>
          <ExpansionPanelDetails bottom>
            <Grid
              ref={this.refCallback}
              container
              item
              className={classes.root}
              justifyContent="center"
              spacing={1}
            >
              <CertSnackBar
                open={this.state.openSnackBar}
                message="Narrative Copied"
                closeHandler={this.closeSnackbar}
              />
              <CertHeader
                smallWidget={smallWidget}
                certPhysName={certPhysName}
                verbalTakenBy={
                  // eslint-disable-next-line no-nested-ternary
                  (formVerbalTakenByFirstName || formVerbalTakenByLastName) &&
                  formVerbalTakenByCredentials
                    ? `${formVerbalTakenByFirstName} ${formVerbalTakenByLastName}, ${formVerbalTakenByCredentials}`.trim()
                    : formVerbalTakenByFirstName || formVerbalTakenByLastName
                    ? `${formVerbalTakenByFirstName} ${formVerbalTakenByLastName}`.trim()
                    : null
                }
                currentCert={currentCert}
                doPhysicianPecosCheck={this.doPhysicianPecosCheck}
                {...{
                  classes,
                  headerGrids,
                  benStartDate,
                  benEndDate,
                  formPhysVerbalDate,
                  updates,
                  admitDate,
                  embedded,
                }}
                historicalView={historicalView}
              />
              {face2faceVisit?.visit_id && visitDetails && (
                <>
                  <VisitModal
                    visitDetails={visitDetails}
                    modalOpen={!!visitDetailsOpen}
                    onClick={() => this.setState({ visitDetailsOpen: false })}
                    agency={this.getF2fAgency()}
                    keycloak={keycloak}
                    patientNumber={_patient.patientNumber}
                    ManifestWidget={Manifest}
                  />
                </>
              )}

              {/** should we have a CertFormPrint view?
               * CONS: means if we update the cert view needs to update the print
               * PRO: cleaner */}
              {printConfig && printConfig.view === Listing && (
                <Footer
                  key={`cert-footer-${printFormType}-${selectedCertIndex}`}
                  alreadyCertified={alreadyCertified}
                  certificationDateTime={certificationDateTime}
                  classes={classes}
                  certPhysName={certPhysName}
                  footerType={footerType}
                  formNarrativeId={formNarrativeId}
                  formType={printFormType}
                  hideFooter={hideFooter}
                  selectedCertIndex={selectedCertIndex}
                  updates={updates}
                  smallWidget={smallWidget}
                  trailingMessage={trailingMessage}
                  printConfig={printConfig}
                  historicalView={historicalView}
                />
              )}
              {(!printConfig || (printConfig && printConfig.view === Card)) && (
                <CertBody
                  classes={classes}
                  Footer={
                    <Footer
                      key={`cert-footer-${formType}-${selectedCertIndex}`}
                      alreadyCertified={alreadyCertified}
                      certificationDateTime={certificationDateTime}
                      classes={classes}
                      certPhysName={certPhysName}
                      footerType={footerType}
                      formNarrativeId={formNarrativeId}
                      formType={printConfig ? printFormType : formType}
                      hideFooter={hideFooter}
                      selectedCertIndex={selectedCertIndex}
                      updates={updates}
                      smallWidget={smallWidget}
                      trailingMessage={trailingMessage}
                      printConfig={printConfig}
                      historicalView={historicalView}
                    />
                  }
                  trailingMessage={trailingMessage}
                  embedded={embedded}
                  smallWidget={smallWidget}
                  f2fMissing={f2fDetails.missing}
                  dispatch={this.props.dispatch}
                  certPhysName={certPhysName}
                  certificationDateTime={certificationDateTime}
                  body={body}
                  handleCertify={this.handleCertify}
                  isDoctor={isDoctor}
                  canCertify={canCertify}
                  narrative={this.state.narrative}
                  formatMessage={this.props.intl.formatMessage}
                  errors={this.setErrors(f2fDetails.missing, inDateRange)}
                  alreadyCertified={alreadyCertified}
                  pastingofnarratives={pastingofnarratives}
                  attendingRequired={attendingRequired}
                  additionalnarrative={additionalnarrative}
                  formNarrativeId={formNarrativeId}
                  formPhysVerbalDate={formPhysVerbalDate}
                  formType={printConfig ? printFormType : formType}
                  transferredAndVerified={transferredAndVerified}
                  noNarrative={noNarrative}
                  selectedCertIndex={selectedCertIndex}
                  submitting={submitting}
                  takenBy={takenBy}
                  userId={userId}
                  attendingPhysicianId={attendingPhysicianId}
                  hospiceFormProps={{
                    pasteText: this.pasteText,
                    captureNarrative: this.captureNarrative,
                    copyText: this.copyText,
                    textCopy: this.state.textCopy,
                    certPhysName,
                    f2fDetails,
                    formPhys,
                    historicalView,
                    userId,
                    updates,
                  }}
                  openVisitDetails={() => {
                    this.setState({ visitDetailsOpen: true });
                  }}
                  historicalView={historicalView}
                />
              )}
            </Grid>
          </ExpansionPanelDetails>
        </ExpansionPanel>
      </Fragment>
    );
  }
}

CertForm.propTypes = {
  classes: PropTypes.object.isRequired,
  updates: PropTypes.array,
};

export default injectIntl(withStyles(styles)(withCertContext(CertForm)));
